import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const departments = [
  { value: 'ventas', label: 'Ventas' },
  { value: 'compras', label: 'Compras' },
  { value: 'contraloría', label: 'Contraloría' },
  { value: 'dirección general', label: 'Dirección General' },
  { value: 'bodega', label: 'Bodega' },
  { value: 'seguridad', label: 'Seguridad' },
  { value: 'Capacitación', label: 'Capacitación' },
  { value: 'Constructora', label: 'Constructora' },
  { value: 'Oficina de Bodas', label: 'Oficina de Bodas' },
  { value: 'Mantenimiento', label: 'Mantenimiento' },
  { value: 'Recursos Humanos', label: 'Recursos Humanos' },
  { value: 'Spa', label: 'Spa' },
  { value: 'A y B', label: 'A y B' },
  { value: 'Sistemas', label: 'Sistemas' },
  { value: 'Owner Services', label: 'Owner Services' },
  { value: 'HOA', label: 'HOA' },
  { value: 'Guest Services', label: 'Guest Services' },
  { value: 'División Cuartos', label: 'División Cuartos' },
  { value: 'Proyecto Elite', label: 'Proyecto Elite' },
  { value: 'Selección y Reclutamiento', label: 'Selección y Reclutamiento' },
  { value: 'Auditoría General', label: 'Auditoría General' }
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [department, setDepartment] = useState('ventas');
  const [role, setRole] = useState('user');
  const [createLoading, setCreateLoading] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'areas', or 'reasons'
  const [departmentAreasConfig, setDepartmentAreasConfig] = useState([]);
  const [allAvailableAreas, setAllAvailableAreas] = useState([]);
  const [allAvailableReasons, setAllAvailableReasons] = useState([]);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [showAreasModal, setShowAreasModal] = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  const [showAddAreaModal, setShowAddAreaModal] = useState(false);
  const [newReasonName, setNewReasonName] = useState('');
  const [showAddReasonModal, setShowAddReasonModal] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [showPropertiesModal, setShowPropertiesModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [newUserPropertyIds, setNewUserPropertyIds] = useState([]);
  const { user, logout, token } = useAuth();

  // Check if current user is admin_master
  const isAdminMaster = user?.role?.toLowerCase() === 'admin_master';
  // Check if current user is carlos.loreto (can delete areas/reasons)
  const canDeleteAreas = user?.username === 'carlos.loreto';

  useEffect(() => {
    fetchUsers();
    fetchDepartmentAreas();
    fetchAllAreas();
    fetchAllReasons();
    fetchProperties();
  }, []);

  const fetchDepartmentAreas = async () => {
    try {
      
      const response = await fetch(`${API}/department-areas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartmentAreasConfig(data);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchAllAreas = async () => {
    try {
      
      const response = await fetch(`${API}/department-areas/all-areas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllAvailableAreas(data.areas || []);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchAllReasons = async () => {
    try {
      
      const response = await fetch(`${API}/visit-reasons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllAvailableReasons(data.reasons || []);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchProperties = async () => {
    try {
      
      const response = await fetch(`${API}/properties`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(data || []);
      }
    } catch (error) {
      // Silently handle error
    }
  };

  const handleEditUserProperties = (userItem) => {
    setEditingUserId(userItem.id);
    setSelectedPropertyIds(userItem.property_ids || []);
    setShowPropertiesModal(true);
  };

  const handleSaveUserProperties = async () => {
    if (!editingUserId) return;
    
    try {
      
      const response = await fetch(`${API}/auth/users/${editingUserId}/properties`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedPropertyIds)
      });
      
      if (response.ok) {
        toast.success('Propiedades asignadas correctamente');
        setShowPropertiesModal(false);
        setEditingUserId(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error al asignar propiedades');
    }
  };

  const toggleProperty = (propId) => {
    setSelectedPropertyIds(prev => 
      prev.includes(propId) 
        ? prev.filter(p => p !== propId)
        : [...prev, propId]
    );
  };

  const handleEditDepartmentAreas = (dept) => {
    const config = departmentAreasConfig.find(c => c.department === dept.value);
    setEditingDepartment(dept);
    setSelectedAreas(config?.areas || []);
    setShowAreasModal(true);
  };

  const handleSaveAreas = async () => {
    if (!editingDepartment) return;
    
    try {
      
      const response = await fetch(`${API}/department-areas/${editingDepartment.value}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ areas: selectedAreas })
      });
      
      if (response.ok) {
        toast.success(`Áreas actualizadas para ${editingDepartment.label}`);
        setShowAreasModal(false);
        setEditingDepartment(null);
        fetchDepartmentAreas();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar áreas');
    }
  };

  const toggleArea = (area) => {
    setSelectedAreas(prev => 
      prev.includes(area) 
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const selectAllAreas = () => {
    setSelectedAreas([...allAvailableAreas]);
  };

  const deselectAllAreas = () => {
    setSelectedAreas([]);
  };

  const handleAddNewArea = async () => {
    if (!newAreaName.trim()) {
      toast.error('Ingrese un nombre para el área');
      return;
    }
    
    if (allAvailableAreas.includes(newAreaName.trim())) {
      toast.error('Esta área ya existe');
      return;
    }
    
    try {
      
      const response = await fetch(`${API}/department-areas/add-area`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ area_name: newAreaName.trim() })
      });
      
      if (response.ok) {
        toast.success(`Área "${newAreaName.trim()}" agregada exitosamente`);
        setNewAreaName('');
        setShowAddAreaModal(false);
        fetchAllAreas();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al agregar área');
      }
    } catch (error) {
      toast.error('Error al agregar área');
    }
  };

  const handleDeleteArea = async (areaName) => {
    if (!canDeleteAreas) {
      toast.error('Solo carlos.loreto puede eliminar áreas');
      return;
    }
    
    if (!window.confirm(`¿Está seguro de eliminar el área "${areaName}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      
      const response = await fetch(`${API}/department-areas/remove-area`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ area_name: areaName })
      });
      
      if (response.ok) {
        toast.success(`Área "${areaName}" eliminada exitosamente`);
        fetchAllAreas();
        fetchDepartmentAreas();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar área');
      }
    } catch (error) {
      toast.error('Error al eliminar área');
    }
  };

  const handleAddNewReason = async () => {
    if (!newReasonName.trim()) {
      toast.error('Ingrese un nombre para el motivo');
      return;
    }
    
    if (allAvailableReasons.includes(newReasonName.trim())) {
      toast.error('Este motivo ya existe');
      return;
    }
    
    try {
      
      const response = await fetch(`${API}/visit-reasons/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason_name: newReasonName.trim() })
      });
      
      if (response.ok) {
        toast.success(`Motivo "${newReasonName.trim()}" agregado exitosamente`);
        setNewReasonName('');
        setShowAddReasonModal(false);
        fetchAllReasons();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al agregar motivo');
      }
    } catch (error) {
      toast.error('Error al agregar motivo');
    }
  };

  const handleDeleteReason = async (reasonName) => {
    if (!canDeleteAreas) {
      toast.error('Solo carlos.loreto puede eliminar motivos');
      return;
    }
    
    if (!window.confirm(`¿Está seguro de eliminar el motivo "${reasonName}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      
      const response = await fetch(`${API}/visit-reasons/remove`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason_name: reasonName })
      });
      
      if (response.ok) {
        toast.success(`Motivo "${reasonName}" eliminado exitosamente`);
        fetchAllReasons();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar motivo');
      }
    } catch (error) {
      toast.error('Error al eliminar motivo');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      
      
      if (!token) {
        toast.error('No hay sesión activa. Por favor inicie sesión nuevamente.');
        setUsers([]);
        setLoading(false);
        return;
      }
      
      // Using fetch as a more reliable alternative
      const response = await fetch(`${API}/auth/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Sesión expirada. Por favor inicie sesión nuevamente.');
        } else if (response.status === 403) {
          toast.error('No tiene permisos para ver usuarios.');
        } else {
          toast.error(`Error del servidor: ${response.status}`);
        }
        setUsers([]);
        return;
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setUsers(data);
        if (data.length > 0) {
          toast.success(`${data.length} usuarios cargados`);
        }
      } else {
        setUsers([]);
      }
    } catch (error) {
      toast.error('Error de conexión: ' + error.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    setCreateLoading(true);
    try {
      
      if (!token) {
        toast.error('Sesión expirada. Por favor inicie sesión nuevamente.');
        return;
      }
      
      const response = await axios.post(`${API}/auth/register`, {
        username: username.trim(),
        password: password.trim(),
        department: department,
        role: role,
        property_ids: role === 'admin_master' ? [] : newUserPropertyIds
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data) {
        const propCount = newUserPropertyIds.length;
        toast.success(`Usuario creado exitosamente${propCount > 0 ? ` con ${propCount} propiedad(es)` : ''}`);
        setUsername('');
        setPassword('');
        setDepartment('ventas');
        setRole('user');
        setNewUserPropertyIds([]);
        // Small delay before fetching to ensure DB is updated
        setTimeout(() => {
          fetchUsers();
        }, 500);
      }
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) or simple string errors
      let errorMsg = 'Error al crear usuario';
      const detail = error.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error - extract the message
        errorMsg = detail[0]?.msg || detail[0]?.message || 'Error de validación';
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) {
      return;
    }

    try {
      
      await axios.delete(`${API}/auth/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       (Array.isArray(detail) && detail[0]?.msg) || 'Error al eliminar usuario';
      toast.error(errorMsg);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword.trim()) {
      toast.error('Ingrese una nueva contraseña');
      return;
    }
    
    try {
      
      await axios.put(`${API}/auth/users/${userId}/password`, 
        { new_password: newPassword },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      toast.success('Contraseña actualizada exitosamente');
      setResetPasswordUserId(null);
      setNewPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       (Array.isArray(detail) && detail[0]?.msg) || 'Error al cambiar contraseña';
      toast.error(errorMsg);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExportExcel = async () => {
    try {
      
      const response = await fetch(`${API}/export/excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al exportar');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Sistema_QR_Export_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel descargado exitosamente');
    } catch (error) {
      toast.error('Error al exportar a Excel');
    }
  };

  return (
    <div className="user-management-container" data-testid="user-management">
      <div className="admin-header">
        <div className="header-left">
          <h1 data-testid="users-title">Gestión de Usuarios</h1>
          <p className="user-info">Administrador: <strong>{user?.username}</strong></p>
        </div>
        <div className="header-actions">
          <button 
            onClick={handleExportExcel} 
            className="back-button" 
            style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white'}}
            data-testid="export-excel-btn"
          >
            📊 Exportar Excel
          </button>
          <Link to="/admin" className="back-button" data-testid="back-admin-btn">
            Panel Admin
          </Link>
          <button onClick={logout} className="logout-button" data-testid="logout-btn">
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb',
        maxWidth: '1200px',
        margin: '0 auto 20px auto',
        padding: '0 20px'
      }}>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'users' ? '#667eea' : 'transparent',
            color: activeTab === 'users' ? 'white' : '#6b7280',
            cursor: 'pointer',
            fontWeight: '600',
            borderRadius: '8px 8px 0 0',
            transition: 'all 0.2s'
          }}
          data-testid="tab-users"
        >
          👥 Usuarios
        </button>
        <button
          onClick={() => setActiveTab('areas')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'areas' ? '#667eea' : 'transparent',
            color: activeTab === 'areas' ? 'white' : '#6b7280',
            cursor: 'pointer',
            fontWeight: '600',
            borderRadius: '8px 8px 0 0',
            transition: 'all 0.2s'
          }}
          data-testid="tab-areas"
        >
          🏢 Áreas
        </button>
        <button
          onClick={() => setActiveTab('reasons')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'reasons' ? '#667eea' : 'transparent',
            color: activeTab === 'reasons' ? 'white' : '#6b7280',
            cursor: 'pointer',
            fontWeight: '600',
            borderRadius: '8px 8px 0 0',
            transition: 'all 0.2s'
          }}
          data-testid="tab-reasons"
        >
          📋 Motivos de Visita
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="admin-content">
          <div className="form-section" data-testid="create-user-form">
            <h2>Crear Nuevo Usuario</h2>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label htmlFor="username">Usuario</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Ingrese el nombre de usuario"
                  data-testid="username-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Contraseña</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingrese la contraseña"
                  data-testid="password-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="department">Departamento</label>
                <select
                  id="department"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  data-testid="department-select"
                >
                  {departments.map(dept => (
                    <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="role">Rol</label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  data-testid="role-select"
                >
                  <option value="user">Usuario - Puede crear QR</option>
                  <option value="security">Seguridad - Solo escanear QR</option>
                  <option value="admin">Administrador - Gestiona su propiedad</option>
                  {isAdminMaster && (
                    <option value="admin_master">Admin Master - Acceso total</option>
                  )}
                </select>
              </div>

              {/* Property Selection for new user */}
              {role !== 'admin_master' && properties.length > 0 && (
                <div className="form-group">
                  <label>🏨 Propiedad(es) Asignada(s)</label>
                  <div style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '10px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: '#f9fafb'
                  }}>
                    {properties.map(prop => (
                      <div 
                        key={prop.id}
                        onClick={() => {
                          setNewUserPropertyIds(prev => 
                            prev.includes(prop.id) 
                              ? prev.filter(p => p !== prop.id)
                              : [...prev, prop.id]
                          );
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px',
                          background: newUserPropertyIds.includes(prop.id) ? '#dbeafe' : 'white',
                          border: newUserPropertyIds.includes(prop.id) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          borderRadius: '6px',
                          marginBottom: '6px',
                          cursor: 'pointer'
                        }}
                        data-testid={`prop-checkbox-${prop.code}`}
                      >
                        <input
                          type="checkbox"
                          checked={newUserPropertyIds.includes(prop.id)}
                          onChange={() => {}}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>{prop.name}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{prop.code}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                    {newUserPropertyIds.length === 0 
                      ? 'Sin propiedad asignada (acceso limitado)' 
                      : `${newUserPropertyIds.length} propiedad(es) seleccionada(s)`}
                  </p>
                </div>
              )}

              <button
                type="submit"
                className="submit-button"
                disabled={createLoading}
                data-testid="create-user-btn"
              >
                {createLoading ? 'Creando...' : 'Crear Usuario'}
              </button>
            </form>
          </div>

          <div className="list-section" data-testid="users-list">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
              <h2 style={{margin: 0}}>Usuarios Registrados ({users.length})</h2>
              <button 
                onClick={fetchUsers} 
                className="back-button"
                style={{padding: '8px 16px'}}
              >
                🔄 Actualizar
              </button>
            </div>
            {loading ? (
              <div className="no-visitors">
                <div className="empty-state-icon">⏳</div>
                <p>Cargando usuarios...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="no-visitors">
                <div className="empty-state-icon">👥</div>
                <p>No hay usuarios registrados</p>
                <button onClick={fetchUsers} className="submit-button" style={{marginTop: '10px'}}>
                  Recargar
                </button>
              </div>
            ) : (
              users.map((userItem) => (
                <div key={userItem.id} className="visitor-card" data-testid={`user-card-${userItem.id}`}>
                  <div className="visitor-info">
                    <div>
                      <div className="visitor-name" data-testid={`user-name-${userItem.id}`}>
                        {userItem.username}
                      </div>
                      <div className="visitor-tags">
                        <span className="area-badge" data-testid={`user-dept-${userItem.id}`}>
                        {userItem.department}
                        </span>
                        {userItem.role === 'admin_master' && (
                          <span className="visitor-type" style={{background: '#fef3c7', color: '#b45309'}}>
                            ADMIN MASTER
                          </span>
                        )}
                        {userItem.role === 'admin' && (
                          <span className="visitor-type" style={{background: '#fee', color: '#dc2626'}}>
                            ADMIN
                          </span>
                        )}
                        {userItem.role === 'security' && (
                          <span className="visitor-type" style={{background: '#e0f2fe', color: '#0369a1'}}>
                            SEGURIDAD
                          </span>
                        )}
                        {userItem.role === 'user' && (
                          <span className="visitor-type" style={{background: '#f0fdf4', color: '#166534'}}>
                            USUARIO
                          </span>
                        )}
                        {userItem.property_ids?.length > 0 && (
                          <span className="visitor-type" style={{background: '#f3e8ff', color: '#7c3aed'}}>
                            {userItem.property_ids.length} propiedades
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="visitor-meta">
                    <p data-testid={`user-created-${userItem.id}`}>
                      Creado: {formatDate(userItem.created_at)}
                    </p>
                  </div>

                  <div className="visitor-actions" style={{flexDirection: 'column', gap: '8px'}}>
                    {resetPasswordUserId === userItem.id ? (
                      <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                        <input
                          type="password"
                          placeholder="Nueva contraseña"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          style={{flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd'}}
                        />
                        <button
                          className="submit-button"
                          onClick={() => handleResetPassword(userItem.id)}
                          style={{padding: '8px 12px', fontSize: '12px'}}
                        >
                          ✓
                        </button>
                        <button
                          className="back-button"
                          onClick={() => {setResetPasswordUserId(null); setNewPassword('');}}
                          style={{padding: '8px 12px', fontSize: '12px'}}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                        <button
                          className="back-button"
                          onClick={() => setResetPasswordUserId(userItem.id)}
                          style={{flex: 1, padding: '8px'}}
                          data-testid={`reset-pwd-btn-${userItem.id}`}
                        >
                          🔑 Cambiar Contraseña
                        </button>
                        {isAdminMaster && userItem.role !== 'admin_master' && (
                          <button
                            className="back-button"
                            onClick={() => handleEditUserProperties(userItem)}
                            style={{padding: '8px 12px', background: '#8b5cf6', color: 'white'}}
                            data-testid={`assign-props-btn-${userItem.id}`}
                          >
                            🏢
                          </button>
                        )}
                        {userItem.id !== user?.id && (
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteUser(userItem.id)}
                            data-testid={`delete-user-btn-${userItem.id}`}
                            style={{padding: '8px 12px'}}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'areas' && (
        <div className="admin-content">
          <div className="form-section" style={{maxWidth: '100%'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <div>
                <h2 style={{margin: '0 0 8px 0'}}>Configurar Áreas por Departamento</h2>
                <p style={{color: '#6b7280', margin: 0}}>
                  Seleccione las áreas que cada departamento podrá ver al crear visitantes. 
                </p>
              </div>
              <button
                onClick={() => setShowAddAreaModal(true)}
                style={{
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
                data-testid="add-area-btn"
              >
                ➕ Agregar Nueva Área
              </button>
            </div>

            {/* Global Areas List */}
            <div style={{
              background: '#f0f9ff',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                <h3 style={{margin: 0, fontSize: '14px', fontWeight: '600', color: '#0369a1'}}>
                  🌐 Áreas Globales Disponibles ({allAvailableAreas.length})
                </h3>
                {canDeleteAreas && (
                  <span style={{fontSize: '12px', color: '#6b7280', fontStyle: 'italic'}}>
                    🔑 Puedes eliminar áreas
                  </span>
                )}
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {allAvailableAreas.map(area => (
                  <div
                    key={area}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <span>{area}</span>
                    {canDeleteAreas && (
                      <button
                        onClick={() => handleDeleteArea(area)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '0 2px',
                          fontSize: '14px',
                          lineHeight: 1
                        }}
                        title="Eliminar área"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <h3 style={{margin: '0 0 16px 0', fontSize: '16px', color: '#374151'}}>Áreas por Departamento</h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '16px'
            }}>
              {departments.map(dept => {
                const config = departmentAreasConfig.find(c => c.department === dept.value);
                const areaCount = config?.areas?.length || 0;
                
                return (
                  <div 
                    key={dept.value}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '16px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.2s'
                    }}
                    data-testid={`dept-card-${dept.value}`}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                      <h3 style={{margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937'}}>
                        {dept.label}
                      </h3>
                      <span style={{
                        background: areaCount > 0 ? '#e0f2fe' : '#fef3c7',
                        color: areaCount > 0 ? '#0369a1' : '#92400e',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {areaCount} áreas
                      </span>
                    </div>
                    
                    {areaCount > 0 && (
                      <div style={{marginBottom: '12px', maxHeight: '80px', overflow: 'hidden'}}>
                        <p style={{fontSize: '13px', color: '#6b7280', margin: 0}}>
                          {config.areas.slice(0, 3).join(', ')}
                          {config.areas.length > 3 && ` y ${config.areas.length - 3} más...`}
                        </p>
                      </div>
                    )}
                    
                    <button
                      onClick={() => handleEditDepartmentAreas(dept)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                      data-testid={`edit-areas-btn-${dept.value}`}
                    >
                      ✏️ Editar Áreas
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Reasons Tab */}
      {activeTab === 'reasons' && (
        <div className="admin-content">
          <div className="form-section" style={{maxWidth: '100%'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <div>
                <h2 style={{margin: '0 0 8px 0'}}>Gestionar Motivos de Visita</h2>
                <p style={{color: '#6b7280', margin: 0}}>
                  Administre los motivos disponibles para las visitas.
                </p>
              </div>
              <button
                onClick={() => setShowAddReasonModal(true)}
                style={{
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
                data-testid="add-reason-btn"
              >
                ➕ Agregar Nuevo Motivo
              </button>
            </div>

            {/* Reasons List */}
            <div style={{
              background: '#fef3c7',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #fcd34d'
            }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                <h3 style={{margin: 0, fontSize: '16px', fontWeight: '600', color: '#92400e'}}>
                  📋 Motivos de Visita Disponibles ({allAvailableReasons.length})
                </h3>
                {canDeleteAreas && (
                  <span style={{fontSize: '12px', color: '#6b7280', fontStyle: 'italic'}}>
                    🔑 Puedes eliminar motivos
                  </span>
                )}
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '12px'
              }}>
                {allAvailableReasons.map(reason => (
                  <div
                    key={reason}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'white',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                    data-testid={`reason-item-${reason}`}
                  >
                    <span style={{fontWeight: '500', color: '#374151'}}>{reason}</span>
                    {canDeleteAreas && (
                      <button
                        onClick={() => handleDeleteReason(reason)}
                        style={{
                          background: '#fee2e2',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          fontSize: '16px',
                          lineHeight: 1,
                          borderRadius: '6px',
                          marginLeft: '10px'
                        }}
                        title="Eliminar motivo"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {allAvailableReasons.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#6b7280'
                }}>
                  <p style={{fontSize: '16px', margin: 0}}>No hay motivos registrados</p>
                  <p style={{fontSize: '14px', margin: '8px 0 0 0'}}>Haz clic en &quot;Agregar Nuevo Motivo&quot; para crear uno</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Areas Selection Modal */}
      <Dialog open={showAreasModal} onOpenChange={setShowAreasModal}>
        <DialogContent style={{maxWidth: '700px', maxHeight: '80vh', overflow: 'auto'}}>
          <DialogHeader>
            <DialogTitle>Editar Áreas - {editingDepartment?.label}</DialogTitle>
            <DialogDescription>
              Seleccione las áreas que los usuarios de este departamento podrán ver
            </DialogDescription>
          </DialogHeader>
          
          <div style={{marginBottom: '16px', display: 'flex', gap: '10px'}}>
            <button
              onClick={selectAllAreas}
              style={{
                padding: '8px 16px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              ✓ Seleccionar Todas
            </button>
            <button
              onClick={deselectAllAreas}
              style={{
                padding: '8px 16px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              ✕ Deseleccionar Todas
            </button>
            <span style={{marginLeft: 'auto', color: '#6b7280', alignSelf: 'center'}}>
              {selectedAreas.length} seleccionadas
            </span>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '8px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '4px'
          }}>
            {allAvailableAreas.map(area => (
              <label
                key={area}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 12px',
                  background: selectedAreas.includes(area) ? '#e0f2fe' : '#f9fafb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: selectedAreas.includes(area) ? '2px solid #0369a1' : '1px solid #e5e7eb',
                  transition: 'all 0.15s'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedAreas.includes(area)}
                  onChange={() => toggleArea(area)}
                  style={{width: '18px', height: '18px', accentColor: '#667eea'}}
                />
                <span style={{fontSize: '13px', color: '#374151'}}>{area}</span>
              </label>
            ))}
          </div>
          
          <div style={{display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end'}}>
            <button
              onClick={() => setShowAreasModal(false)}
              style={{
                padding: '10px 20px',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveAreas}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
              data-testid="save-areas-btn"
            >
              💾 Guardar Cambios
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Area Modal */}
      <Dialog open={showAddAreaModal} onOpenChange={setShowAddAreaModal}>
        <DialogContent style={{maxWidth: '450px'}}>
          <DialogHeader>
            <DialogTitle>➕ Agregar Nueva Área</DialogTitle>
            <DialogDescription>
              Ingrese el nombre del área que desea agregar al sistema
            </DialogDescription>
          </DialogHeader>
          
          <div style={{padding: '20px 0'}}>
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151'}}>
                Nombre del Área
              </label>
              <input
                type="text"
                value={newAreaName}
                onChange={(e) => setNewAreaName(e.target.value)}
                placeholder="Ej: Sala de Juntas, Terraza, etc."
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px'
                }}
                data-testid="new-area-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddNewArea();
                  }
                }}
              />
            </div>
            
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  setShowAddAreaModal(false);
                  setNewAreaName('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddNewArea}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                data-testid="confirm-add-area-btn"
              >
                ➕ Agregar Área
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Reason Modal */}
      <Dialog open={showAddReasonModal} onOpenChange={setShowAddReasonModal}>
        <DialogContent style={{maxWidth: '450px'}}>
          <DialogHeader>
            <DialogTitle>➕ Agregar Nuevo Motivo</DialogTitle>
            <DialogDescription>
              Ingrese el nombre del motivo de visita que desea agregar
            </DialogDescription>
          </DialogHeader>
          
          <div style={{padding: '20px 0'}}>
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151'}}>
                Nombre del Motivo
              </label>
              <input
                type="text"
                value={newReasonName}
                onChange={(e) => setNewReasonName(e.target.value)}
                placeholder="Ej: Capacitación, Evento, Mantenimiento..."
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px'
                }}
                data-testid="new-reason-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddNewReason();
                  }
                }}
              />
            </div>
            
            <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  setShowAddReasonModal(false);
                  setNewReasonName('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddNewReason}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                data-testid="confirm-add-reason-btn"
              >
                ➕ Agregar Motivo
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Properties Assignment Modal */}
      <Dialog open={showPropertiesModal} onOpenChange={setShowPropertiesModal}>
        <DialogContent style={{maxWidth: '500px'}}>
          <DialogHeader>
            <DialogTitle>Asignar Propiedades</DialogTitle>
            <DialogDescription>
              Seleccione las propiedades a las que este usuario tendrá acceso.
            </DialogDescription>
          </DialogHeader>
          <div style={{maxHeight: '400px', overflowY: 'auto', marginTop: '15px'}}>
            {properties.map(prop => (
              <div 
                key={prop.id}
                onClick={() => toggleProperty(prop.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: selectedPropertyIds.includes(prop.id) ? '#f0fdf4' : '#f9fafb',
                  border: selectedPropertyIds.includes(prop.id) ? '2px solid #10b981' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  cursor: 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPropertyIds.includes(prop.id)}
                  onChange={() => {}}
                  style={{width: '18px', height: '18px'}}
                />
                <div>
                  <div style={{fontWeight: '600'}}>{prop.name}</div>
                  <div style={{fontSize: '12px', color: '#6b7280'}}>{prop.code} - {prop.address}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}>
            <button
              onClick={() => setShowPropertiesModal(false)}
              style={{
                flex: 1,
                padding: '12px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveUserProperties}
              style={{
                flex: 1,
                padding: '12px',
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
              data-testid="save-properties-btn"
            >
              Guardar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
